import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/feedback';
import { Field, Input } from '../../components/ui/form';
import { errorMessage, fieldErrors, useAuthStatus, useLogin } from '../../lib/session';
import { AuthLayout } from './auth-layout';

export function LoginPage() {
  const status = useAuthStatus();
  const login = useLogin();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (status.data?.setupRequired) return <Navigate to="/setup" replace />;
  if (status.data?.authenticated && !login.isPending) return <Navigate to="/" replace />;

  const errors = fieldErrors(login.error);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          void navigate('/', { replace: true });
        },
      },
    );
  };

  return (
    <AuthLayout title="Sign in" description="Access your market research workspace.">
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {login.error ? <Alert tone="error">{errorMessage(login.error)}</Alert> : null}
        <Field label="Email" error={errors['email']} required>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password" error={errors['password']} required>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={login.isPending}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
